import { UserPlus } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/_shared/components/ui/dialog';
import { Label } from '@/_shared/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/_shared/components/ui/select';

export type LookoutRole = 'member' | 'admin';

/** A person of the organization who is not in this Lookout workspace (the second table, or the invite answer). */
export interface InviteCandidate {
  rsUserId: string;
  email: string;
  name: string | null;
  organizationRole: 'admin' | 'member';
  /** The person is a member of this workspace's RudderStack workspace. */
  inSibling: boolean;
}

interface InviteOrganizationMemberDialogProps {
  candidate: InviteCandidate | null;
  /** True when the invite dialog found the email in the organization (LLD §5.2). */
  fromInvite: boolean;
  initialRole: LookoutRole;
  pending: boolean;
  onConfirm: (input: { rsUserId: string; role: LookoutRole }) => void;
  onClose: () => void;
}

/** What the three grant sets of a new Lookout admin let the person do. They get them when they join (way 2). */
const ADMIN_GRANTS = 'permission to create, delete and edit audiences and activations';

/**
 * What RudderStack also gives the person when they join, or null when it gives nothing. RudderStack changes nothing
 * at the invite, so a cancelled or expired invite gives nothing (owner decision of 29 Oct 2026). An RS admin needs no
 * membership and gets no grants. A person who is not in this workspace's RudderStack workspace gets that membership. A
 * new Lookout admin also gets the three grant sets, and a Lookout member gets none.
 */
function rudderstackNote(candidate: InviteCandidate, role: LookoutRole): string | null {
  if (candidate.organizationRole === 'admin') return null;
  const onJoin = `When ${candidate.email} joins, RudderStack gives them`;
  if (!candidate.inSibling) {
    const access = `${onJoin} access to this workspace in RudderStack`;
    return role === 'admin'
      ? `${access}, and ${ADMIN_GRANTS}. RudderStack sends no email.`
      : `${access}, with no permissions. RudderStack sends no email.`;
  }
  return role === 'admin' ? `${onJoin} ${ADMIN_GRANTS} in this workspace.` : null;
}

/**
 * Invite an organization member (ACT2-1124): Lookout sends its own invite email, with the role that the admin picks.
 * When they join, a person who is not a RudderStack admin and not in this workspace's RudderStack workspace gets that
 * membership, and a new Lookout admin also gets the three grant sets. RudderStack sends no email for it, so the note
 * follows the role.
 */
export function InviteOrganizationMemberDialog({
  candidate,
  fromInvite,
  initialRole,
  pending,
  onConfirm,
  onClose,
}: InviteOrganizationMemberDialogProps): React.ReactElement {
  const [role, setRole] = useState<LookoutRole>(initialRole);
  const name = candidate ? (candidate.name ?? candidate.email) : '';
  const note = candidate ? rudderstackNote(candidate, role) : null;

  return (
    <Dialog
      open={candidate !== null}
      onOpenChange={(open) => {
        if (!open && !pending) onClose();
      }}
    >
      <DialogContent
        onEscapeKeyDown={(event) => {
          if (pending) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (pending) event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Invite to this workspace</DialogTitle>
          <DialogDescription>
            {fromInvite
              ? `${name} is already in the RudderStack organization. Lookout sends them an invite email.`
              : `Lookout sends ${name} an invite email. They join this workspace when they accept it.`}
          </DialogDescription>
        </DialogHeader>
        {note ? <Callout variant="info">{note}</Callout> : null}
        <div className="grid gap-2">
          <Label htmlFor="invite-member-role">Lookout role</Label>
          <Select value={role} onValueChange={(value) => setRole(value as LookoutRole)}>
            <SelectTrigger id="invite-member-role" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="member">Member</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button type="button" variant="secondary" disabled={pending} onClick={onClose} label="Cancel" />
          <Button
            type="button"
            variant="primary"
            Icon={<UserPlus />}
            loading={pending}
            onClick={() => candidate && onConfirm({ rsUserId: candidate.rsUserId, role })}
            label="Send invite"
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
